// src/pages/Users/Lexique/hooks/useLexiqueController.js

import { useCallback, useEffect, useMemo, useState } from "react";

import { getSettings } from "../../../../services/settingsServices";
import { getLexique } from "../../../../services/lexiqueServices";
import { getLexiqueKarate } from "../../../../services/lexiquekarateServices";

import {
  DEFAULT_LEXIQUE_UI,
  FAV_KEY,
  LEXIQUE_UI_STORAGE_KEY,
  buildAvailableLetters,
  buildLexiqueGroups,
  exportLexiqueCSV,
  filterAndSortLexique,
  readJsonStorage,
} from "../utils/lexiqueUtils";

export default function useLexiqueController() {
  const [discipline, setDiscipline] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [ui, setUi] = useState(() => {
    const saved = readJsonStorage(LEXIQUE_UI_STORAGE_KEY, null);

    return saved ? { ...DEFAULT_LEXIQUE_UI, ...saved } : DEFAULT_LEXIQUE_UI;
  });

  const [searchInput, setSearchInput] = useState(ui.q || "");
  const [shareCopied, setShareCopied] = useState(false);

  const [favorites, setFavorites] = useState(() => {
    const saved = readJsonStorage(FAV_KEY, []);

    return Array.isArray(saved) ? saved : [];
  });

  const persistUi = useCallback((nextState) => {
    setUi(nextState);
    localStorage.setItem(LEXIQUE_UI_STORAGE_KEY, JSON.stringify(nextState));
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((prev) => {
      const next = {
        ...prev,
        ...patch,
      };

      localStorage.setItem(LEXIQUE_UI_STORAGE_KEY, JSON.stringify(next));

      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUi((prev) => {
        if (prev.q === searchInput) return prev;

        const next = {
          ...prev,
          q: searchInput,
        };

        localStorage.setItem(LEXIQUE_UI_STORAGE_KEY, JSON.stringify(next));

        return next;
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let mounted = true;

    async function fetchLexique() {
      setLoading(true);
      setError("");

      try {
        const settings = await getSettings();
        const currentDiscipline = settings?.[0]?.discipline_nom || "Judo";

        const service = currentDiscipline === "Karate" ? getLexiqueKarate : getLexique;

        const raw = await service();

        if (!mounted) return;

        setDiscipline(currentDiscipline);
        setData(Array.isArray(raw) ? raw.filter(Boolean) : []);
      } catch (err) {
        console.error("Erreur chargement lexique :", err);

        if (!mounted) return;

        setData([]);
        setError("Impossible de charger le lexique pour le moment.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchLexique();

    return () => {
      mounted = false;
    };
  }, []);

  const availableLetters = useMemo(() => buildAvailableLetters(data), [data]);

  const filtered = useMemo(
    () => filterAndSortLexique(data, ui, favorites),
    [data, ui, favorites]
  );

  const groups = useMemo(
    () => buildLexiqueGroups(filtered, ui.letter),
    [filtered, ui.letter]
  );

  const resetFilters = useCallback(() => {
    persistUi(DEFAULT_LEXIQUE_UI);
    setSearchInput(DEFAULT_LEXIQUE_UI.q);
  }, [persistUi]);

  const copyShare = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (ui.q) params.set("q", ui.q);
      if (ui.letter !== DEFAULT_LEXIQUE_UI.letter) {
        params.set("letter", ui.letter);
      }
      if (ui.sort !== DEFAULT_LEXIQUE_UI.sort) {
        params.set("sort", ui.sort);
      }
      if (ui.favOnly) params.set("fav", "1");

      const query = params.toString();

      const url = `${window.location.origin}${window.location.pathname}${
        query ? `?${query}` : ""
      }`;

      await navigator.clipboard.writeText(url);

      setShareCopied(true);

      setTimeout(() => {
        setShareCopied(false);
      }, 1400);
    } catch {
      // Non bloquant.
    }
  }, [ui]);

  const exportCSV = useCallback(() => {
    exportLexiqueCSV(filtered, discipline || "judo");
  }, [filtered, discipline]);

  const toggleFavorite = useCallback((japonais) => {
    if (!japonais) return;

    setFavorites((prev) => {
      const next = prev.includes(japonais)
        ? prev.filter((item) => item !== japonais)
        : [...prev, japonais];

      localStorage.setItem(FAV_KEY, JSON.stringify(next));

      return next;
    });
  }, []);

  return {
    discipline,
    loading,
    error,
    data,
    filtered,
    groups,
    availableLetters,
    favorites,
    ui,
    searchInput,
    setSearchInput,
    updateUi,
    resetFilters,
    copyShare,
    shareCopied,
    exportCSV,
    toggleFavorite,
  };
}